import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, LogOut, Menu, PanelLeftClose, PanelLeft, X } from 'lucide-react';
import { Logo, LogoLockup } from './Logo';
import { ThemeToggle } from './theme';
import { cn } from './ui';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import type { NavGroup, NavItem } from '../nav';

/**
 * The application shell: a grouped sidebar, a top bar, and the page.
 *
 * The sidebar is the primary navigation because this product has far more screens than a
 * horizontal bar can hold — the previous top-nav fit six items and the feature set is already
 * past that. Groups collapse so the list stays scannable, and the whole rail collapses to icons
 * for narrow screens without losing access to anything.
 */
export function Shell({
  groups,
  route,
  setRoute,
  email,
  onSignOut,
  children,
}: {
  groups: readonly NavGroup[];
  route: string;
  setRoute: (route: string) => void;
  email: string;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Navigating on a phone should not leave the drawer covering the page you just asked for.
  useEffect(() => setMobileOpen(false), [route]);

  const active = groups.flatMap((g) => g.items).find((i) => i.route === route);
  const activeGroup = groups.find((g) => g.items.some((i) => i.route === route));

  return (
    <div className="flex min-h-screen">
      {/* Scrim: only rendered on mobile, where the sidebar floats above the content. */}
      {mobileOpen && (
        <button
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-sidebar-border bg-sidebar transition-[width,transform] duration-200',
          collapsed ? 'w-[68px]' : 'w-[232px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b border-sidebar-border px-4">
          {collapsed ? <Logo size={24} /> : <LogoLockup size={24} />}
          <button
            className="ml-auto rounded-[--radius] p-1 text-muted-foreground hover:bg-sidebar-accent lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {groups.map((group) => (
            <Group
              key={group.label}
              group={group}
              route={route}
              setRoute={setRoute}
              collapsed={collapsed}
              defaultOpen={activeGroup?.label === group.label}
            />
          ))}
        </nav>

        <div className="hidden border-t border-sidebar-border p-2 lg:block">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="flex w-full items-center gap-2.5 rounded-[--radius] px-2.5 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeft size={17} /> : <PanelLeftClose size={17} />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      <div
        className={cn('flex min-w-0 flex-1 flex-col', collapsed ? 'lg:pl-[68px]' : 'lg:pl-[232px]')}
      >
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur">
          <button
            className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={18} />
          </button>

          {/* Breadcrumb, so a screen deep in a group still says where it sits. */}
          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            {activeGroup && (
              <>
                <span className="truncate text-muted-foreground">{activeGroup.label}</span>
                <span className="text-muted-foreground/50">/</span>
              </>
            )}
            <span className="truncate font-medium">{active?.label ?? ''}</span>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <ThemeToggle />
            <div className="hidden items-center gap-2 sm:flex">
              <span className="max-w-[180px] truncate text-sm text-muted-foreground">{email}</span>
              <button
                onClick={onSignOut}
                className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted"
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}

function Group({
  group,
  route,
  setRoute,
  collapsed,
  defaultOpen,
}: {
  group: NavGroup;
  route: string;
  setRoute: (route: string) => void;
  collapsed: boolean;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  // A group holding the current page opens itself, which matters after a reload lands mid-tree.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  const { Icon } = group;

  // A single-item group is a link, not an accordion: making someone expand a section to reach the
  // one thing inside it is pure friction.
  if (group.items.length === 1) {
    const only = group.items[0]!;
    return (
      <Item
        item={only}
        icon={<Icon size={17} />}
        active={route === only.route}
        collapsed={collapsed}
        label={group.label}
        onSelect={() => setRoute(only.route)}
      />
    );
  }

  if (collapsed) {
    /**
     * One icon per group, with the items in a flyout.
     *
     * This used to render one row per item, each with the group's icon — so a group of four
     * showed the same glyph four times and the rail became a column of identical marks that told
     * you nothing and could not be told apart. An icon rail is only useful if each icon means
     * something different.
     */
    const activeHere = group.items.some((item) => item.route === route);

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            title={group.label}
            aria-label={group.label}
            aria-current={activeHere ? 'page' : undefined}
            className={cn(
              'mb-0.5 flex w-full items-center justify-center rounded-[--radius] px-2.5 py-2 transition-colors',
              activeHere
                ? 'bg-sidebar-active text-sidebar-active-foreground'
                : 'text-sidebar-foreground hover:bg-sidebar-accent',
            )}
          >
            <Icon size={17} className="shrink-0" />
          </button>
        </PopoverTrigger>

        <PopoverContent side="right" align="start" className="w-56 p-1">
          {/* Named, because an icon alone does not say which section this is. */}
          <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{group.label}</p>
          {group.items.map((item) => (
            <button
              key={item.route}
              onClick={() => setRoute(item.route)}
              aria-current={route === item.route ? 'page' : undefined}
              className={cn(
                'flex w-full items-center rounded-[--radius] px-2 py-1.5 text-sm transition-colors',
                route === item.route
                  ? 'bg-accent font-medium text-accent-foreground'
                  : 'hover:bg-accent',
              )}
            >
              <span className="truncate">{item.label}</span>
            </button>
          ))}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className="mb-0.5">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 rounded-[--radius] px-2.5 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent"
      >
        <Icon size={17} className="shrink-0" />
        <span className="truncate">{group.label}</span>
        <ChevronDown
          size={15}
          className={cn('ml-auto shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="mt-0.5 space-y-0.5">
          {group.items.map((item) => (
            <button
              key={item.route}
              onClick={() => setRoute(item.route)}
              aria-current={route === item.route ? 'page' : undefined}
              className={cn(
                'flex w-full items-center rounded-[--radius] py-1.5 pl-9 pr-2.5 text-sm transition-colors',
                route === item.route
                  ? 'bg-sidebar-active font-medium text-sidebar-active-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent',
              )}
            >
              <span className="truncate">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Item({
  item,
  icon,
  active,
  collapsed,
  label,
  onSelect,
}: {
  item: NavItem;
  icon: ReactNode;
  active: boolean;
  collapsed: boolean;
  label: string;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? label : undefined}
      className={cn(
        'mb-0.5 flex w-full items-center gap-2.5 rounded-[--radius] px-2.5 py-2 text-sm transition-colors',
        collapsed && 'justify-center',
        active
          ? 'bg-sidebar-active font-medium text-sidebar-active-foreground'
          : 'text-sidebar-foreground hover:bg-sidebar-accent',
      )}
    >
      <span className="shrink-0">{icon}</span>
      {!collapsed && <span className="truncate">{item.label}</span>}
    </button>
  );
}
